import { notify } from './notify';
import { ASSET_VERSION } from './version';

const BRAND_HOST = 'eparic';

export interface IConfig {
  cognito_userPoolId: string;
  cognito_userPoolWebClientId: string;
}

/**
 * Build service URLs dynamically from current window location
 * Example: https://dev.portal.admin.eparic.com
 * - API: https://dev.portal.admin.api.eparic.com
 * - Auth: https://dev.portal.admin.auth.eparic.com
 * - WebSocket: https://dev.portal.admin.ws.eparic.com
 */
export function buildServiceUrl(service: 'api' | 'auth' | 'ws'): string {
  try {
    const currentHost = window.location.host;
    const protocol = window.location.protocol;

    // For local development (both localhost and local.eparic.com), use specific fallbacks
    if (currentHost.includes('localhost') || currentHost.startsWith('local.eparic.com')) {
      switch (service) {
        case 'auth':
          return '/secure/';
        case 'api':
          return '/api/'; // Will be proxied by vite middleware
        case 'ws':
          return `ws://${currentHost}/ws/`;
        default:
          return '/';
      }
    }

    // For production domains ending with .eparic.com
    if (currentHost.includes('.eparic.com')) {
      const parts = currentHost.split('.');
      if (parts.length >= 3) {
        // Find the position of BRAND_HOST and insert the service before it
        const brandIndex = parts.findIndex(part => part === BRAND_HOST);
        if (brandIndex >= 0) {
          parts.splice(brandIndex, 0, service);
          const serviceHost = parts.join('.');
          const baseUrl = `${protocol}//${serviceHost}`;
          // AWS API Gateway WebSocket still uses https, not wss
          return service === 'auth' ? `${baseUrl}/callback` : baseUrl;
        }
      }
    }

    // Fallback for unexpected domains
    switch (service) {
      case 'auth':
        return '/secure/';
      case 'api':
        return '/api/';
      case 'ws':
        return '/ws/';
      default:
        return '/';
    }
  } catch (error) {
    console.warn(`Failed to build ${service} URL from current location, using fallback`, error);
    return service === 'auth' ? '/secure/' : `/${service}/`;
  }
}

/**
 * Get the auth endpoint URL
 */
export function getAuthEndpoint(): string {
  return buildServiceUrl('auth');
}

/**
 * Get the API base URL
 */
export function getApiBaseUrl(): string {
  return buildServiceUrl('api');
}

/**
 * Get the WebSocket URL
 */
export function getWebSocketUrl(): string {
  return buildServiceUrl('ws');
}

let cachedConfig: IConfig | null = null;

export async function getConfig(): Promise<IConfig> {
  if (cachedConfig) return cachedConfig;

  try {
    // Note: In production (e.g., CloudFront), missing or error responses for /config.json 
    // will typically result in non-200 status codes, and response.ok will correctly be false.
    // However, in Vite's dev server, it may return 200 OK even if the file doesn't exist,
    // often serving an HTML fallback. So we must also manually parse the text to detect invalid JSON.
    const response = await fetch(`${import.meta.env.BASE_URL}config.json?v=${ASSET_VERSION}`);
    if (!response.ok) {
      throw new Error(`Failed to load config.json: ${response.status}`);
    }

    const text = await response.text();

    cachedConfig = JSON.parse(text);
    return cachedConfig!;
  } catch (error) {
    notify('error', 'Failed to load config.json', 'Config Error');
    throw error;
  }
}